import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase/cliente'

export type ItemSugerenciaInventario = {
  id: string
  codigo?: string
  id_inventario?: number | string
  descripcion: string
  unidad?: string
  existencias?: number | null
  tipo: 'material' | 'herramienta'
}

/**
 * Detecta unidad de medida técnica según tipo de producto y descripción.
 * Corrige productos que World Office cataloga genéricamente como "Und." pero son KG, M, GL, etc.
 */
export function detectarUnidadMaterial(descripcion: string, unidad?: string | null): string {
  if (unidad && !/^\s*(und\.?|unidad)\s*$/i.test(unidad)) {
    return unidad.trim().toUpperCase()
  }
  const d = (descripcion || '').toLowerCase()
  if (/\b(cable|alambre|cord[oó]n|manguera|cinta|reata|tuber[ií]a|tubo)\b/.test(d)) return 'M'
  if (
    /\b(soldadura|fundente|electrodo|bentonita|cemento conductivo|qu[ií]mico|grasa|estaño|polvo)\b/.test(d) ||
    /\bkg\b|\bkilogram/i.test(d)
  ) {
    return 'KG'
  }
  if (/\b(gal[oó]n|caneca|litro|thinner|tiner|pintura|esmalte)\b/.test(d)) return 'GL'
  if (/\b(caja|paquete|bolsa|bulto|rollo)\b/.test(d)) return 'PAQ'
  if (/\b(juego|kit|set)\b/.test(d)) return 'KIT'
  return 'UND'
}

export function useBuscarInventario(tipo: 'material' | 'herramienta', termino: string) {
  const [sugerencias, setSugerencias] = useState<ItemSugerenciaInventario[]>([])
  const [buscando, setBuscando] = useState(false)

  useEffect(() => {
    const q = termino.trim()
    if (q.length < 2) {
      setSugerencias([])
      setBuscando(false)
      return
    }

    let activo = true
    setBuscando(true)

    const timer = setTimeout(async () => {
      try {
        if (tipo === 'material') {
          const { data, error } = await supabase
            .from('vista_inventario')
            .select('id_inventario, codigo, nombre, descripcion, unidad, existencias')
            .ilike('descripcion', `%${q}%`)
            .limit(8)

          if (!error && data && activo) {
            setSugerencias(
              data.map((item: any) => ({
                id: String(item.id_inventario),
                codigo: item.codigo || undefined,
                id_inventario: item.id_inventario,
                descripcion: item.nombre || item.descripcion,
                unidad: item.unidad || 'UND',
                existencias: item.existencias,
                tipo: 'material',
              }))
            )
          }
        } else {
          const { data, error } = await supabase
            .from('herramientas')
            .select('id, codigo, nombre, cantidad_total, cantidad_disponible')
            .eq('activo', true)
            .ilike('nombre', `%${q}%`)
            .limit(8)

          if (!error && data && activo) {
            setSugerencias(
              data.map((item: any) => ({
                id: item.id,
                codigo: item.codigo || undefined,
                descripcion: item.nombre,
                unidad: 'UND',
                existencias: item.cantidad_disponible ?? item.cantidad_total,
                tipo: 'herramienta',
              }))
            )
          }
        }
      } catch {
        if (activo) setSugerencias([])
      } finally {
        if (activo) setBuscando(false)
      }
    }, 220)

    return () => {
      activo = false
      clearTimeout(timer)
    }
  }, [tipo, termino])

  return { sugerencias, buscando }
}

export type ItemInventarioMaterial = {
  id: string | number
  codigo: string | null
  nombre: string
  descripcion: string
  unidad: string
  existencias: number | null
  agotado?: boolean
  se_inventaria?: boolean
}

export function useCatalogoInventarioMateriales(termino: string, soloConStock = false) {
  const [items, setItems] = useState<ItemInventarioMaterial[]>([])
  const [cargando, setCargando] = useState(false)

  useEffect(() => {
    let activo = true
    setCargando(true)

    const timer = setTimeout(async () => {
      try {
        let peticion = supabase
          .from('vista_inventario')
          .select('id_inventario, codigo, nombre, descripcion, existencias, agotado, se_inventaria')
          .eq('sin_nombre', false)

        if (soloConStock) {
          peticion = peticion.gt('existencias', 0)
        }

        const q = termino.trim()
        if (q) {
          peticion = peticion.or(`descripcion.ilike.%${q}%,codigo.ilike.%${q}%`)
        } else {
          peticion = peticion.order('existencias', { ascending: false, nullsFirst: false })
        }

        const { data, error } = await peticion.limit(25)

        if (!error && data && activo) {
          setItems(
            data.map((row: any) => {
              const desc = row.descripcion || row.nombre || ''
              return {
                id: row.id_inventario,
                codigo: row.codigo || null,
                nombre: row.nombre || row.descripcion,
                descripcion: desc,
                unidad: detectarUnidadMaterial(desc, row.unidad),
                existencias: row.existencias,
                agotado: Boolean(row.agotado),
                se_inventaria: Boolean(row.se_inventaria),
              }
            })
          )
        }
      } catch {
        if (activo) setItems([])
      } finally {
        if (activo) setCargando(false)
      }
    }, termino.trim() ? 250 : 0)

    return () => {
      activo = false
      clearTimeout(timer)
    }
  }, [termino, soloConStock])

  return { items, cargando }
}
